import { CONST } from '@common/constants/const.constant';
import { useField } from '@common/hooks/use-field.hook';
import { useTimeout } from '@common/hooks/use-timeout.hook';
import React, { useCallback, useEffect, useRef } from 'react';
import { ITextAreaV2ChangeHandler, ITextAreaV2Props, TextAreaV2 } from './textarea-v2.component';

export function FormTextAreaV2<TFormValues>(
    props: Omit<ITextAreaV2Props<keyof TFormValues>, 'value' | 'defaultValue'>,
): JSX.Element {
    const { onChange, onBlur, getCustomErrorMessage } = props;
    const { setFieldValue, setFieldTouched, triggerValidate, value, errorMessage } = useField<string>({
        ...props,
        getCustomErrorMessage,
    });
    const refValue = useRef(value);
    useEffect(() => {
        refValue.current = value;
    }, [value]);

    const { start: updateValueWithDelay } = useTimeout<string>(
        useCallback(
            (newValue: string) => {
                refValue.current = newValue;
                setFieldValue(newValue);
                triggerValidate();
            },
            [setFieldValue, triggerValidate],
        ),
        CONST.DEBOUNCE,
    );

    const handleChange = useCallback<ITextAreaV2ChangeHandler>(
        (e) => {
            refValue.current = e.target.value;
            onChange && onChange(e);

            updateValueWithDelay(e.target.value);
        },
        [onChange, updateValueWithDelay],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLTextAreaElement>) => {
            onBlur && onBlur(e);
            setFieldValue(refValue.current);
            setFieldTouched();
        },
        [onBlur, setFieldTouched, setFieldValue],
    );

    return (
        <TextAreaV2
            errorMessage={errorMessage}
            {...props}
            onChange={handleChange}
            onBlur={handleBlur}
            name={props.name as string}
            value={value}
        />
    );
}
